// domain/model/Policyholder.js
import { POLICYHOLDER_RELATIONSHIP } from '../constants/coverage';

/**
 * Who holds the policy a coverage bills against, and what they are to the client.
 *
 * A value object rather than two fields on the coverage, because the rule worth protecting is about
 * the PAIR. Held apart they drift, and the drift is silent: a coverage claiming self while pointing
 * at somebody else answers "is this person a policyholder" wrongly for every reader, and nothing
 * about it looks wrong.
 *
 * SELF IS AN ANSWER, NOT A DEFAULT. Every saved coverage names a policyholder, and self is one of the
 * ways to name one, so no reader has to branch on whether there is one. But an unanswered coverage is
 * a real state and it is not self: in ABA the client is usually a child and the policyholder usually a
 * parent, so assuming self would quietly record the less likely answer for anyone who did not look at
 * the field. An unset relationship is therefore held as unset, exactly as an unset funding source is,
 * and `validateCoverage` refuses to save it.
 *
 * Only the PERSON ID is null for self. Storing the client's own id here would be a second copy of
 * something we already know, and it would make every self-coverage answer yes to "is this person
 * somebody's policyholder" once that question is asked of the collection.
 *
 * A REFERENCE, NEVER A SNAPSHOT. No demographics are copied onto the coverage; see Coverage.types
 * for why.
 *
 * NO `@references`, deliberately. `personId` names a person, and a person is not an entry in this
 * context's catalogue: it belongs to another area of the product, and the only thing held here is a
 * shared identifier, referenced and never interpreted. `PolicyholderDemographics` is this context's
 * read of that person, not the thing being referenced, so pointing at it here would be wrong.
 *
 * BELONGS TO THE COVERAGE, not to either party. The same human can hold the policy on one coverage
 * and be a dependent on another at the same time, so this is not a property of the person and not a
 * property of the client.
 *
 * @domainObject value-object
 * @invariant `personId` is null whenever the relationship is self, and the factory enforces that by
 *   nulling an id handed in alongside it. The converse it cannot fix, so a named relationship with no
 *   person is reported by `validateCoverage` rather than silently turned back into self, which would
 *   record the wrong payer relationship.
 * @invariant An unset relationship is not self and is not saveable. Only `validateCoverage` says so;
 *   the factory holds the unanswered state rather than resolving it, the same way it holds a null
 *   funding source.
 * @invariant Immutable. Repointing a coverage produces a new policyholder; nothing edits one in
 *   place. Same as `EligibilityVerification`.
 * @typedef {Object} Policyholder
 * @property {string|null} personId - the person who holds the policy; null when that is the client
 * @property {string|null} relationship - POLICYHOLDER_RELATIONSHIP, in the user's direction
 */

/**
 * Builds a Policyholder, answering nothing that was not answered.
 *
 * Accepts a partial so it serves both "new, nothing chosen yet" and "hydrate from storage". An absent
 * relationship stays absent: a blank coverage has not said the client holds their own policy, it has
 * said nothing, and those are different records.
 *
 * @param {{ personId?: string|null, relationship?: string|null }} [attrs]
 * @returns {Policyholder}
 */
export function createPolicyholder(attrs = {}) {
  const relationship = attrs.relationship ?? null;

  return {
    personId: relationship === POLICYHOLDER_RELATIONSHIP.SELF
      ? null
      : (attrs.personId ?? null),
    relationship,
  };
} // createPolicyholder()

/**
 * Is the client their own policyholder?
 *
 * Asked of the pair so callers do not reach past the value object to compare a relationship string
 * against an enum. Readiness needs exactly this question rather than the relationship itself: what
 * a payer requires differs depending on whether the policyholder is also the patient.
 *
 * @param {Policyholder} policyholder
 * @returns {boolean}
 */
export function isSelf(policyholder) {
  return policyholder?.relationship === POLICYHOLDER_RELATIONSHIP.SELF;
} // isSelf()
